import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBackendUrl } from "@src/utils/getBackendUrl";
import {
	DEFAULT_INVENTORY_DISPLAY_MODE,
	InventoryDisplayMode,
} from "@src/utils/inventoryDisplayMode";

interface UnmappedEventsByMarketplace {
	GO_TICKETS: number;
	SEAT_GEEK: number;
	TICKET_MASTER: number;
	TICKET_NETWORK: number;
	VIA_GOGO: number;
}

export interface StatisticsState {
	totalUnmappedEvents: number;
	unmappedEventsByMarketplace: UnmappedEventsByMarketplace;
	totalAutoMappedEvents: number;
	totalSleptEvents: number;
	loading: "idle" | "pending" | "succeeded" | "failed";
	error: string | null;
	inventoryDisplayMode: InventoryDisplayMode;
}

const initialState: StatisticsState = {
	totalUnmappedEvents: 0,
	unmappedEventsByMarketplace: {
		GO_TICKETS: 0,
		SEAT_GEEK: 0,
		TICKET_MASTER: 0,
		TICKET_NETWORK: 0,
		VIA_GOGO: 0,
	},
	totalAutoMappedEvents: 0,
	totalSleptEvents: 0,
	loading: "idle",
	error: null,
	inventoryDisplayMode: DEFAULT_INVENTORY_DISPLAY_MODE,
};

export const fetchStatistics = createAsyncThunk(
	"statistics/fetchStatistics",
	async (
		{ inventoryDisplayMode, includeInactive }:
			{ inventoryDisplayMode?: InventoryDisplayMode; includeInactive?: boolean } = {},
		{ rejectWithValue },
	) => {
		try {
			const token = sessionStorage.getItem("token");
			const headers = { Authorization: `Bearer ${token}` };

			const [unmappedEventsResponse, autoMappedEventsResponse, sleptEventsResponse] = await Promise.all([
				axios.get(`${getBackendUrl()}/events/total-unmapped`, {
					headers,
					params: {
						inventoryDisplayMode,
						includeInactive,
					},
				}),
				axios.get(`${getBackendUrl()}/events/auto-mapped`, { headers }),
				axios.get(`${getBackendUrl()}/events/slept`, { headers }),
			]);

			return {
				totalUnmappedEvents: unmappedEventsResponse.data.total_unmapped_events,
				unmappedEventsByMarketplace: unmappedEventsResponse.data.unmapped_events_by_marketplace,
				totalAutoMappedEvents: autoMappedEventsResponse.data.total_auto_mapped_events,
				totalSleptEvents: sleptEventsResponse.data.total_slept_events,
				inventoryDisplayMode,
			};
		} catch (error) {
			return rejectWithValue("Failed to fetch statistics");
		}
	},
);

export const statisticsSlice = createSlice({
	name: "statistics",
	initialState,
	reducers: {
		setInventoryDisplayMode: (state, action) => {
			state.inventoryDisplayMode = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchStatistics.pending, (state) => {
				state.loading = "pending";
			})
			.addCase(fetchStatistics.fulfilled, (state, action) => {
				const nextUnmappedEventsByMarketplace = {
					...action.payload.unmappedEventsByMarketplace,
				};

				state.loading = "succeeded";
				state.inventoryDisplayMode = action.payload.inventoryDisplayMode ?? DEFAULT_INVENTORY_DISPLAY_MODE;
				state.unmappedEventsByMarketplace = nextUnmappedEventsByMarketplace;
				state.totalUnmappedEvents = action.payload.totalUnmappedEvents;
				state.totalAutoMappedEvents = action.payload.totalAutoMappedEvents;
				state.totalSleptEvents = action.payload.totalSleptEvents;
			})
			.addCase(fetchStatistics.rejected, (state, action) => {
				state.loading = "failed";
				state.error = action.payload as string;
			});
	},
});

export default statisticsSlice.reducer;
export const { setInventoryDisplayMode } = statisticsSlice.actions;
